import { useContext, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import {
  Box,
  Button,
  Flex,
  ListWithPagination,
  Text,
  mainTheme,
  PaginationContext,
  ModalContext,
} from 'dash-ui';

import {
  Amenity,
  GetPropertyByIdQuery,
  useGetAmenitiesQuery,
} from 'gql/generated.graphql';

import AmenityItem from 'containers/properties/PropertyFeaturesAndAmenities/components/AmenityItem/AmenityItem';
import { PropertyFeaturesAndAmenityTabModalsEnum } from 'containers/properties/PropertyFeaturesAndAmenities/constants';

const PER_PAGE = 12;
const listItemSizes = {
  minWidth: 260,
  gap: 24,
};

const AmenitiesWrapper = styled(Flex)`
  & > div {
    & > div:first-child {
      padding: 0 ${({ theme }) => theme.space * 6}px;
    }
  }
  .amenities_container {
    flex-wrap: wrap;
    & > div {
      flex-grow: 1;
    }
  }
`;

const HeaderFlex = styled(Flex)`
  border-bottom: 1px solid ${mainTheme.colors.n30};
`;

function FeaturesAndAmenities({
  property,
}: {
  property: GetPropertyByIdQuery['getPropertyById'];
}) {
  const listRef = useRef(null);
  const theme = useTheme();
  const { setPayload, toggleModal } = useContext(ModalContext);
  const {
    setTotal,
    setPerPage,
    state: paginationState,
    getRefetchProps,
  } = useContext(PaginationContext);
  const { skip, limit } = getRefetchProps(paginationState);

  const { t: tPropertyPage } = useTranslation('pages', {
    keyPrefix: 'propertyPage',
  });
  const { t: tAmenitiesTable } = useTranslation('tables', {
    keyPrefix: 'amenities',
  });
  const { t: tCommonLabels } = useTranslation('common', {
    keyPrefix: 'labels',
  });

  const {
    data: result,
    refetch,
    loading,
  } = useGetAmenitiesQuery({
    variables: {
      pagination: { skip, limit },
      sort: [{ fieldName: 'name', sortOrder: 1 }],
      filter: { propertyId: property._id },
    },
  });
  const { data, total } = useMemo(
    () => ({
      data: result?.getAmenities?.collection || [],
      total: result?.getAmenities?.totalCount || 0,
    }),
    [result]
  );

  const onClickEditIcon = (amenity: Amenity) => {
    setPayload({ property, amenity });
    toggleModal(
      true,
      PropertyFeaturesAndAmenityTabModalsEnum.EDIT_AMENITY_NAME_IN_PROPERTY_MODAL
    );
  };

  const onClickTrashIcon = (amenity: Amenity) => {
    setPayload({ property, amenity });
    toggleModal(
      true,
      PropertyFeaturesAndAmenityTabModalsEnum.REMOVE_AMENITY_MODAL
    );
  };

  const onClickManageAmenitiesButton = () => {
    setPayload({ property });
    toggleModal(
      true,
      PropertyFeaturesAndAmenityTabModalsEnum.MANAGE_AMENITIES_MODAL
    );
  };

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    setTotal(total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  useLayoutEffect(() => {
    // calculate count of items base on list width
    const newPerPage = listRef.current
      ? Math.trunc(
          // @ts-ignore
          (listRef.current.clientWidth - theme.space * 6 * 2) /
            (listItemSizes.minWidth + listItemSizes.gap)
        ) * 3
      : 0;
    setPerPage(Math.max(newPerPage, PER_PAGE));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  useEffect(() => {
    refetch({
      pagination: { skip, limit },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, limit]);

  return (
    <Box w="100%">
      <HeaderFlex
        px={6}
        py={4}
        w="100%"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Text lg>{tPropertyPage`propertyFeaturesAndAmenities`}</Text>
        <Button
          p={3}
          icon="plus"
          variation="primary"
          medium
          onClick={onClickManageAmenitiesButton}
        >
          {tPropertyPage`manageAmenities`}
        </Button>
      </HeaderFlex>

      <AmenitiesWrapper ref={listRef} w="100%">
        <ListWithPagination
          listProps={{
            data,
            loading,
            grid: listItemSizes.minWidth,
            gap: listItemSizes.gap,
            placeholder: tAmenitiesTable('noAmenities') as string,
            containerClassName: 'amenities_container',
            my: 2,
          }}
          paginationProps={{
            label: tAmenitiesTable`paginationLabel` || '',
            previousButtonLabel: tCommonLabels`previous`,
            nextButtonLabel: tCommonLabels`next`,
          }}
        >
          {(amenity: Amenity) => (
            <AmenityItem
              amenity={amenity}
              onClickTrashIcon={() => onClickTrashIcon(amenity)}
              onClickEditIcon={() => onClickEditIcon(amenity)}
            />
          )}
        </ListWithPagination>
      </AmenitiesWrapper>
    </Box>
  );
}

export default FeaturesAndAmenities;
